import React, { useEffect, useMemo, useState } from 'react';
import Card from '../../components/common/Card';
import Spinner from '../../components/common/Spinner';
import { providersAPI } from '../../api/providers';
import { bookingsAPI } from '../../api/bookings';
import { authAPI } from '../../api/auth';
import {
  ChartBarIcon,
  ArrowTrendingUpIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
  BuildingStorefrontIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import { formatCurrency } from '../../utils/formatter';

const STATUS_COLORS = {
  Completed: '#22c55e',
  Upcoming: 'var(--orange-9)',
  Cancelled: '#e5484d',
};

const bookingAmount = (b) =>
  Number(b.subtotal || b.totalPaid || b.totalPrice || b.amount || b.price || 0);

const bookingDate = (b) => {
  const raw = b.startAt || b.scheduledAt || b.createdAt;
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
};

/* ── Chart primitives (same SVG style as the dashboards) ── */

const BarChart = ({ data, money = false, color = 'var(--orange-9)' }) => {
  const max = Math.max(1, ...data.map((d) => d.value));
  const width = 520;
  const height = 170;
  const padX = 28;
  const padTop = 14;
  const padBottom = 34;
  const usableW = width - padX * 2;
  const usableH = height - padTop - padBottom;
  const barW = usableW / data.length - 14;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
      {[0.25, 0.5, 0.75, 1].map((t) => (
        <line
          key={t}
          x1={padX}
          x2={width - padX}
          y1={padTop + usableH * (1 - t)}
          y2={padTop + usableH * (1 - t)}
          stroke="var(--gray-4)"
          strokeDasharray="3 3"
        />
      ))}
      {data.map((d, i) => {
        const h = (d.value / max) * usableH;
        const x = padX + i * (usableW / data.length) + 7;
        const y = padTop + usableH - h;
        return (
          <g key={d.label}>
            <rect x={x} y={y} width={barW} height={h} rx={6} fill={color} opacity={d.value === 0 ? 0.15 : 0.95}>
              <title>{`${d.label}: ${money ? formatCurrency(d.value) : d.value}`}</title>
            </rect>
            <text x={x + barW / 2} y={height - padBottom + 18} textAnchor="middle" fontSize="11" fill="var(--gray-10)">
              {d.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

const Donut = ({ segments, total, centerLabel }) => {
  const size = 150;
  const stroke = 20;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  let offset = 0;

  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--gray-4)" strokeWidth={stroke} />
        {segments.map((s, i) => {
          const frac = total > 0 ? s.value / total : 0;
          const len = frac * c;
          const el = (
            <circle
              key={i}
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke={s.color}
              strokeWidth={stroke}
              strokeDasharray={`${len} ${c - len}`}
              strokeDashoffset={-offset}
            />
          );
          offset += len;
          return el;
        })}
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontSize: '22px', fontWeight: 700, color: 'var(--gray-12)', lineHeight: 1 }}>{total}</span>
        <span style={{ fontSize: '11px', color: 'var(--gray-10)', marginTop: '2px' }}>{centerLabel}</span>
      </div>
    </div>
  );
};

const SectionTitle = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-2 mb-4">
    {Icon && <Icon style={{ width: '18px', height: '18px', color: 'var(--orange-9)' }} />}
    <h3 style={{ fontSize: 'var(--font-size-small)', fontWeight: 600, color: 'var(--gray-12)', margin: 0, fontFamily: "'Poppins', sans-serif" }}>
      {children}
    </h3>
  </div>
);

const Kpi = ({ label, value, sub, icon: Icon, tint, iconColor }) => (
  <Card className="dashboard-stat-card" style={{ padding: 'clamp(10px, 2.5vw, 14px)' }}>
    <div className="dashboard-stat-card__top flex min-w-0 items-center gap-2 sm:gap-3 mb-1.5 sm:mb-3">
      <div
        className="dashboard-stat-card__icon"
        style={{
          width: 'clamp(26px, 6vw, 32px)', height: 'clamp(26px, 6vw, 32px)', borderRadius: '8px',
          backgroundColor: tint, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}
      >
        <Icon className="dashboard-stat-card__icon-svg" style={{ width: 'clamp(13px, 3.2vw, 16px)', height: 'clamp(13px, 3.2vw, 16px)', color: iconColor }} />
      </div>
      <span className="dashboard-stat-card__label text-wrap-safe" style={{ fontSize: 'clamp(11px, 2.8vw, 12px)', color: 'var(--gray-10)' }}>{label}</span>
    </div>
    <p className="dashboard-stat-card__value text-wrap-safe" style={{ fontSize: 'clamp(17px, 4.5vw, 21px)', fontWeight: 600, color: 'var(--gray-12)', margin: 0, textAlign: 'left', fontFamily: "'Poppins', sans-serif", lineHeight: 1.1 }}>
      {value}
    </p>
    {sub && (
      <p className="dashboard-stat-card__sub text-wrap-safe" style={{ fontSize: 'clamp(10px, 2.6vw, 11px)', color: 'var(--gray-10)', margin: '3px 0 0', textAlign: 'left' }}>
        {sub}
      </p>
    )}
  </Card>
);

const AdminAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [providers, setProviders] = useState([]);
  const [userStats, setUserStats] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [bookingsData, providersData, users] = await Promise.all([
          bookingsAPI.getBookings({}).catch(() => []),
          providersAPI.getAllProviders().catch(() => providersAPI.getProviders()).catch(() => []),
          authAPI.adminStats().catch(() => null),
        ]);
        setBookings(Array.isArray(bookingsData) ? bookingsData : (bookingsData.bookings || []));
        const list = providersData.providers || providersData;
        setProviders(Array.isArray(list) ? list : []);
        setUserStats(users);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const analytics = useMemo(() => {
    const completed = bookings.filter((b) => b.status === 'COMPLETED');
    const upcoming = bookings.filter((b) => ['PENDING', 'CONFIRMED', 'PAID'].includes(b.status));
    const cancelled = bookings.filter((b) => ['CANCELLED', 'DECLINED', 'NO_SHOW'].includes(b.status));

    const grossRevenue = completed.reduce((s, b) => s + bookingAmount(b), 0);
    const commission = completed.reduce((s, b) => s + Number(b.commission || 0), 0);

    // Last 6 months buckets
    const now = new Date();
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({
        label: d.toLocaleString('en-US', { month: 'short' }),
        year: d.getFullYear(),
        month: d.getMonth(),
      });
    }
    const revenueByMonth = months.map((m) => ({
      label: m.label,
      value: completed
        .filter((b) => {
          const d = bookingDate(b);
          return d && d.getFullYear() === m.year && d.getMonth() === m.month;
        })
        .reduce((s, b) => s + bookingAmount(b), 0),
    }));
    const bookingsByMonth = months.map((m) => ({
      label: m.label,
      value: bookings.filter((b) => {
        const d = bookingDate(b);
        return d && d.getFullYear() === m.year && d.getMonth() === m.month;
      }).length,
    }));

    // Top providers by completed revenue
    const byProvider = new Map();
    completed.forEach((b) => {
      const name = b.provider?.businessName || b.provider?.name || 'Unknown provider';
      const entry = byProvider.get(name) || { name, revenue: 0, count: 0 };
      entry.revenue += bookingAmount(b);
      entry.count += 1;
      byProvider.set(name, entry);
    });
    const topProviders = [...byProvider.values()].sort((a, b) => b.revenue - a.revenue).slice(0, 5);

    return { completed, upcoming, cancelled, grossRevenue, commission, revenueByMonth, bookingsByMonth, topProviders };
  }, [bookings]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="large" />
      </div>
    );
  }

  const donutSegments = [
    { label: 'Completed', value: analytics.completed.length, color: STATUS_COLORS.Completed },
    { label: 'Upcoming', value: analytics.upcoming.length, color: STATUS_COLORS.Upcoming },
    { label: 'Cancelled', value: analytics.cancelled.length, color: STATUS_COLORS.Cancelled },
  ];
  const maxTop = Math.max(1, ...analytics.topProviders.map((p) => p.revenue));

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-xl sm:text-2xl font-bold text-dark mb-1">Analytics</h1>
          <p className="text-gray-600 text-sm">Live platform performance — computed from real bookings, providers, and users</p>
        </div>

        {/* KPIs */}
        <div className="dashboard-stats-grid grid grid-cols-2 xl:grid-cols-4 gap-2.5 sm:gap-4 mb-4 sm:mb-6">
          <Kpi
            label="Gross Revenue"
            value={formatCurrency(analytics.grossRevenue)}
            sub="Completed bookings value"
            icon={CurrencyDollarIcon}
            tint="var(--orange-3)"
            iconColor="var(--orange-9)"
          />
          <Kpi
            label="Platform Commission"
            value={formatCurrency(analytics.commission)}
            sub="8% of completed bookings"
            icon={ArrowTrendingUpIcon}
            tint="#dcfce7"
            iconColor="#16a34a"
          />
          <Kpi
            label="Total Bookings"
            value={bookings.length}
            sub={`${analytics.upcoming.length} upcoming`}
            icon={CalendarDaysIcon}
            tint="#dbeafe"
            iconColor="#3b82f6"
          />
          <Kpi
            label="Total Users"
            value={userStats?.totalUsers ?? '—'}
            sub={userStats
              ? `${userStats.clients} clients · ${userStats.providers} providers`
              : `${providers.length} providers on platform`}
            icon={UserGroupIcon}
            tint="#fef9c3"
            iconColor="#eab308"
          />
        </div>

        {/* Revenue + status */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4 sm:mb-6">
          <Card className="lg:col-span-2">
            <SectionTitle icon={CurrencyDollarIcon}>Revenue — last 6 months</SectionTitle>
            {analytics.revenueByMonth.some((m) => m.value > 0) ? (
              <BarChart data={analytics.revenueByMonth} money />
            ) : (
              <p className="text-sm text-gray-500 py-10 text-center">No completed bookings yet — revenue will chart here.</p>
            )}
          </Card>
          <Card>
            <SectionTitle icon={ChartBarIcon}>Bookings by status</SectionTitle>
            <div className="flex items-center gap-5">
              <Donut segments={donutSegments} total={bookings.length} centerLabel="Bookings" />
              <div className="flex flex-col gap-2">
                {donutSegments.map((s) => (
                  <div key={s.label} className="flex items-center gap-2">
                    <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: s.color, flexShrink: 0 }} />
                    <span style={{ fontSize: '12px', color: 'var(--gray-11)' }}>
                      {s.label} · <strong>{s.value}</strong>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </div>

        {/* Trends row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4 sm:mb-6">
          <Card className="lg:col-span-2">
            <SectionTitle icon={CalendarDaysIcon}>Bookings — last 6 months</SectionTitle>
            {analytics.bookingsByMonth.some((m) => m.value > 0) ? (
              <BarChart data={analytics.bookingsByMonth} color="#3b82f6" />
            ) : (
              <p className="text-sm text-gray-500 py-10 text-center">No bookings yet.</p>
            )}
          </Card>
          <Card>
            <SectionTitle icon={UserGroupIcon}>New users — last 6 months</SectionTitle>
            {userStats?.monthlySignups?.some((m) => m.value > 0) ? (
              <BarChart data={userStats.monthlySignups} color="#eab308" />
            ) : (
              <p className="text-sm text-gray-500 py-10 text-center">No signups in this window.</p>
            )}
          </Card>
        </div>

        {/* Top providers */}
        <Card>
          <SectionTitle icon={BuildingStorefrontIcon}>Top providers by revenue</SectionTitle>
          {analytics.topProviders.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">No completed bookings yet — top earners will rank here.</p>
          ) : (
            <div className="flex flex-col gap-3">
              {analytics.topProviders.map((p) => (
                <div key={p.name}>
                  <div className="flex items-center justify-between mb-1">
                    <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--gray-12)' }} className="truncate">{p.name}</span>
                    <span style={{ fontSize: '12px', color: 'var(--gray-10)', flexShrink: 0 }}>
                      {formatCurrency(p.revenue)} · {p.count} booking{p.count !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div style={{ height: '8px', borderRadius: '999px', backgroundColor: 'var(--gray-3)', overflow: 'hidden' }}>
                    <div
                      style={{
                        width: `${Math.max(4, (p.revenue / maxTop) * 100)}%`,
                        height: '100%',
                        borderRadius: '999px',
                        background: 'linear-gradient(90deg, var(--orange-9), #FFB236)',
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default AdminAnalytics;
